import { Component, OnChanges, Input, Output, EventEmitter, ElementRef, SecurityContext } from '@angular/core';

import {CDSNode, Rule} from '../services/capd.model';
import { RulesService } from '../services/rules.service';
import { DomSanitizer } from "@angular/platform-browser";
import { Marked } from "@ts-stack/markdown";
import { Package } from "../services/capd.model";
import { PackageService } from '../services/package.service';
import {OrgService} from "../services/org.service";
import {NavigationStart, Router} from "@angular/router";
import {AlertService} from "../services/alert.service";

declare var $: any;

@Component({
  selector: 'rule-detail',
  templateUrl: './rule-detail.component.html',
  styleUrls: ['./rule-detail.component.less'],
})

export class RuleDetailComponent implements OnChanges {

  @Input() ruleDetail: Rule;
  @Input() newChange;
  @Output() listChange: EventEmitter<any> = new EventEmitter();
  @Output() deletedRule: EventEmitter<any> = new EventEmitter();

  ruleClone: Rule;
  rules: Rule[];
  categories: string[];
  showPreview = false;
  ruleChanged = false;
  packages_names: any;
  loading: boolean;
  renderEnvironmentList: string[] = [];

  ruleDeleted = new EventEmitter();
  isDisabled = true;

  triggerEdited = new EventEmitter();
  newTrigger = '';
  oldId = '';
  triggerValid = false;

  constructor(
    private _service: RulesService,
    private _el: ElementRef,
    private _sanitize: DomSanitizer,
    private _pkg_service: PackageService,
    private _org_service: OrgService,
    private _router: Router,
    private alertService: AlertService
  ) { }

  ngOnInit() {
    this._router.events.subscribe((event: any) => {
      if(this.ruleChanged && event.url !== this._router.routerState.snapshot.url && event instanceof NavigationStart) {
        if(confirm("You are about to leave the page without saving; all unsaved changes will be lost. Would you like to proceed and abandon current changes?")){
          // continue nav by setting ruleChanged to false
          this.ruleChanged = false
        }
        else{
          // do not leave current page
          this._router.navigateByUrl(this._router.routerState.snapshot.url)
        }
      }

    })
  }

  /**
   * Called whenever a new rule is selected.  Does the following:
   * - Clones the rule to preserve the original version.
   * - Makes sure that a rule has an action, and creates empty actions for rules that do not.
   * - Sets the ruleChanged boolean to false.
   * - Returns packages containing selected rule.
   *
   * @func ngOnChanges
   */
  ngOnChanges() {
    this.packages_names = '';
    this.loading = true;
    let temp = new Set();
    this.ruleClone = JSON.parse(JSON.stringify(this.ruleDetail));
    if (!this.ruleClone.action) {
      this.ruleClone.action = {
        actionType: this.getActionType(),
        messageTitle: null,
        messageText: null,
        messagePriority: 'Normal' };
    }
    if (!this.ruleClone.renderEnvironment) {
      this.renderEnvironmentList.push('Default');
    }
    else {
      this.renderEnvironmentList = this.ruleClone.renderEnvironment.split(", ")
    }
    this.ruleChanged = false;
    this._pkg_service.getPackageByRule(this.ruleClone._id).then(data => {
      this.packages_names = data;
      this.loading = false;
    } )

  }

  getActionType() {
    let actionType = '';
    const renderEnvironment = this.renderEnvironmentList.join(', ');
    switch (renderEnvironment) {
      case 'Default':
      case 'Hcc':
      case 'Default, Hcc':
        actionType = 'Message';
        break;
      case 'Notereader':
        actionType = 'Instruction';
        break;
      case 'Default, Notereader':
      case 'Hcc, Notereader':
      case 'Default, Hcc, Notereader':
        actionType = 'Message, Instruction';
        break;
      default:
        actionType = 'Message';
    }

    return actionType;
  }



  /**
   * Toggle checkbox that disables the delete button by default.
   *
   * @func toggleDisabled
   */
  toggleDisabled() {
    this.isDisabled = !this.isDisabled;
  }

  /**
   * Deletes the rule.
   *
   * @async
   * @func onDelete
   */
  onDelete() {
    this._service.deleteRule(this.ruleClone).then(() => {
      this.ruleDeleted.emit(null);
      this.deletedRule.emit(null);
    });
    this.closeDeleteModal();
  }

  /**
   * Passes changes to the list of rules whenever a rule is modified or deleted.
   *
   * @func passChanges
   * @param {any} change - either an ID string (of new rule to select) or a null object.
   */
  passChanges(change) {
    this.listChange.emit(change);
  }

  /**
   * Toggle to show the preview card.
   *
   * @func togglePreview
   */
  togglePreview() {
    this.showPreview = !this.showPreview;
  }

  /**
   * Toggle to show the rule deletion modal.
   *
   * @func showDelete
   */
  showDeleteModal() {
    $(this._el.nativeElement).find('#modal-delete-rule').show();
  }

  closeDeleteModal() {
    $(this._el.nativeElement).find('#modal-delete-rule').hide();
  }

  /**
   * Toggle to show the trigger editing modal.
   *
   * @func showTriggerEdit
   */
  showTriggerEdit() {
    $(this._el.nativeElement).find('#modal-edit-trigger').show();
  }

  hideTriggerEdit() {
    $(this._el.nativeElement).find('#modal-edit-trigger').hide();
  }

  /**
   * Changes the render environment of the given rule.
   *
   * Depending on the render environment, the following needs to be done:
   * - check if the environment is already set properly, and if so make no changes
   * - otherwise, set the render environment
   * - if the environment uses identical syntax to another environment (i.e. FD and HCC), you'll need to check
   *   if the previous environment used a different syntax.  If so, you'll need to reset the rule's action to a default state.
   * - If the environment has a unique syntax, you'll need to reset the rule's action as well, but won't need to bother checking.
   *
   * @func changeEnv
   * @param {string} env - the new environment to change to, given to the switch statement.
   */
  changeEnv(env: string) {
    const prevEnvList = this.renderEnvironmentList;

    switch (env) {
      case 'FD':
        if (prevEnvList.indexOf('Default') > -1) {
          this.renderEnvironmentList.splice(prevEnvList.indexOf('Default'), 1);
          break;
        }
        this.renderEnvironmentList.push('Default')
        break;
      case 'Hcc':
        if (prevEnvList.indexOf('Hcc') > -1) {
          this.renderEnvironmentList.splice(prevEnvList.indexOf('Hcc'), 1)
          break;
        }
        this.renderEnvironmentList.push('Hcc');
        break;
      case 'Epic':
        if (prevEnvList.indexOf('Notereader') > -1) {
          this.renderEnvironmentList.splice(prevEnvList.indexOf('Notereader'), 1)
          break;
        }
        this.renderEnvironmentList.push('Notereader');
        this.ruleClone.action.actionType = this.getActionType();
        this.ruleClone.action.actionSubtype = undefined;
        this.ruleClone.action.groups = [
          {type: 'Text', messageText: this.ruleClone.action.messageText, options: null},
          {type: 'Radio', messageText: null, options: ['']}
        ];
        break;
    }
  }

  updateDocLevelCheck() {
    this.ruleClone.docLevel = !this.ruleClone.docLevel
  }

  /**
   * Saves the rule data and returns it to the list to be updated.
   *
   * @async
   * @func onSave
   */
  onSave() {
    this.ruleClone.renderEnvironment = this.renderEnvironmentList.join(', ');

    if (this.ruleClone.action.actionType.includes('Message') && !this.ruleClone.action.actionType.includes('Instruction')) {
      this.ruleClone.action.groups = undefined;
    }
    if (!this.ruleClone.action.actionType.includes('Message') && this.renderEnvironmentList.indexOf('Default') < 0 && this.renderEnvironmentList.indexOf('Hcc') < 0) {
      this.ruleClone.action.messageText = undefined;
    }

    if(!this.ruleClone.action.messageText && !this.ruleClone.action.groups[0].messageText) {
      this.alertService.showAlert('Message text is required in order to save');
      return;
    }

    this._service.updateRule(this.ruleClone).then(() => {this.passChanges(this.ruleClone)}).then(()=>{
      this.ruleChanged = false;
      this.alertService.showAlert('Saved Successfully')
    });
  }

  /**
   * Checks to see if the rule is different from how it was before.
   *
   * @func isEdited
   */
  isEdited() {
    this.ruleClone.renderEnvironment = this.renderEnvironmentList.join(', ');
    this.ruleChanged = !(JSON.stringify(this.ruleClone) === JSON.stringify(this.ruleDetail));
  }

  /**
   * Submits the changes to the server.
   *
   * @async
   * @func onSubmit
   */
  onSubmit() {
    this.ruleClone.triggerCode = this.newTrigger;
    this.oldId = this.ruleClone._id;
    this.ruleClone._id = '';

    this._service.deleteRule(this.oldId).then(() => {
      this._service.addRule(this.ruleClone).then(newTrigger => {
        this.triggerEdited.emit(newTrigger);
      });
    });
  }

  /**
   * Checks to see if the new trigger is nonzero and different from the previous version.
   *
   * @func isValid
   */
  isValid() {
    this.triggerValid = !(this.ruleClone.triggerCode === this.newTrigger) && this.newTrigger.length > 0;
  }


  /**
   * Parses the title for the card, using Angular's safe sanitization.
   *
   * @func titlePase
   * @param {string} toParse - the raw text to parse into a displayable title.
   * @returns {string} the safely sanitized title.
   */
  titleParse(toParse: string) {
    if (!!toParse) {
      return this._sanitize.sanitize(SecurityContext.HTML, this.ensureSafeBlankTargetLinks(toParse));
    }
  }

  /**
   * Parses the message of the card, both using HTML and Markdown with safe sanitization.
   *
   * @func messageParse
   * @param {string} toParse - the raw text to parse into a displayable message.
   * @returns {string} the safely sanitized message.
   */
  messageParse(toParse: string) {
    if (!!toParse) {
      const markdownHtml = Marked.parse(toParse);
      return this._sanitize.sanitize(SecurityContext.HTML, this.ensureSafeBlankTargetLinks(markdownHtml));
    }
  }

  private ensureSafeBlankTargetLinks(html: string): string {
    return html.replace(/<a\b([^>]*\btarget\s*=\s*(['"])_blank\2[^>]*)>/gi, (fullMatch, attrs) => {
      const relMatch = attrs.match(/\brel\s*=\s*(['"])(.*?)\1/i);
      if (!relMatch) {
        return `<a${attrs} rel="noopener noreferrer">`;
      }

      const quote = relMatch[1];
      const currentRel = relMatch[2];
      const relTokens = currentRel.split(/\s+/).filter(Boolean);

      if (!relTokens.includes('noopener')) {
        relTokens.push('noopener');
      }
      if (!relTokens.includes('noreferrer')) {
        relTokens.push('noreferrer');
      }

      const updatedRel = `rel=${quote}${relTokens.join(' ')}${quote}`;
      return fullMatch.replace(relMatch[0], updatedRel);
    });
  }

  closePreviewModal() {
    this.showPreview = false;
  }
}
